import { navigate } from "@reach/router"
import { ApiError, CenteredLoader } from "@shared/primitive-components"
import { ResolvedFleetEntry, useAddToFleetModern, useManualResolveEntries } from "../../../api/fleet"
import { Policy } from "../../../api/policy"
import { useAccess, usePolicyAndFleet } from "../../../contexts"
import { ConnectFleetNotification } from "../components/connectFleetNotification/ConnectFleetNotification"
import { Feedback } from "../components/feedback/Feedback"

type AddEntriesToFleetProps = {
  entries: ReadonlyArray<ResolvedFleetEntry>
  manualEntries: ReadonlyArray<ResolvedFleetEntry>
  vehicleSetId: string
}

export const AddEntriesToFleet = ({ entries, manualEntries, vehicleSetId }: AddEntriesToFleetProps) => {
  const { policy, reloadPolicy } = usePolicyAndFleet()

  const onFinish = () => {
    reloadPolicy()
    navigate(`fleet`)
  }

  return (
    <AddEntriesToFleetModern
      onFinish={onFinish}
      policy={policy}
      entries={entries}
      manualEntries={manualEntries}
      vehicleSetId={vehicleSetId}
    />
  )
}

type AddEntriesToFleetModernProps = {
  policy: Policy
  entries: ReadonlyArray<ResolvedFleetEntry>
  onFinish: () => void
  manualEntries: ReadonlyArray<ResolvedFleetEntry>
  vehicleSetId: string
}

const AddEntriesToFleetModern = ({
  policy,
  entries,
  onFinish,
  manualEntries,
  vehicleSetId,
}: AddEntriesToFleetModernProps) => {
  const hasAdminAccess = useAccess().hasAdminAccess()
  const [res] = useAddToFleetModern({
    requestedAccessLevel: hasAdminAccess ? `elevated` : `standard`,
    policyId: policy.id,
    changes: entries.map((entry) => ({
      kind: `add`,
      offCoverDate: entry.offCoverDate,
      onCoverDate: entry.onCoverDate,
      vrn: entry.registrationNumber,
      vehicleId: entry.vehicleId,
      vehicle: {
        make: entry.make,
        model: entry.model,
        abiCode: entry.abiCode ?? null,
        abiGroup: entry.abiGroup ?? null,
        fuelType: entry.fuelType,
        grossWeight: entry.grossWeight ?? null,
        nominalStartYear: entry.nominalStartYear ?? null,
        numberOfSeats: entry.numberOfSeats ?? null,
        trimLevel: entry.trim ?? null,
        type: entry.type,
      },
    })),
  })
  useManualResolveEntries({ entries: manualEntries, vehicleSetId })

  if (res.result?.tag === `err`) {
    // eslint-disable-next-line no-console
    console.error(res.result.error)

    if (res.result.error.type === `INVALID`) {
      switch (res.result.error.payload.type) {
        case `NO_MATCHES`:
          return <ApiError error="VEHICLE_NOT_MATCHED_TO_BAND_LINK" />
        case `INVALID_CHANGE`:
          return <ApiError error="INVALID_CHANGE" />
        default:
          return <ApiError />
      }
    }

    return <ApiError />
  }

  if (res.loading || !res.result) return <CenteredLoader />

  const { length } = entries
  const multipleVehiclesAdded = length !== 1

  return (
    <Feedback
      summary={`We have added ${length} ${multipleVehiclesAdded ? `vehicles` : `vehicle`} to your policy. ${
        multipleVehiclesAdded ? `These cars are` : `This car is`
      } now fully insured and ready to drive. Don't panic if you don't see ${
        multipleVehiclesAdded ? `them` : `it`
      } on the MiD instantly. The site is updated every day at 5am.`}
      additionalMessage={<ConnectFleetNotification onFinish={onFinish} multipleVehiclesAdded={multipleVehiclesAdded} />}
      additionalMessageOffset={false}
      onFinish={onFinish}
      status="success"
    />
  )
}
